import { Link, PageWrapper } from "@/components";
import * as GlobalS from "@/components/styled";
import { DATE_FORMATS, FieldNames, FormType, GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetBaselineDatesQuery, useGetFormLookupsQuery } from "@/services";
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faCirclePlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Space, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { getMonths } from "./helpers";

const dateRange = {
  previousMonthDouble: -12,
  previousMonthSingle: -3,
  current: 0,
  nextMonthSingle: 3,
  nextMonthDouble: 12,
};

const BaselineDates = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  const [projectTypes, setProjectTypes] = useState(null);
  const [offsetMonth, setOffsetMonth] = useState(0);

  const { data: formLookups, isFetching: lookupsFetching } =
    useGetFormLookupsQuery();

  useEffect(() => {
    setProjectTypes(formLookups?.intake?.projectType);
  }, [formLookups]);

  const { data: baselineDates, isFetching: fetchingBaselineDates } =
    useGetBaselineDatesQuery();

  const isLoading = fetchingBaselineDates || lookupsFetching;

  const handleCreateDates = () => {
    navigate(routes.admin.baselineDates.create);
  };

  const handleDateRangeChange = (value) => {
    setOffsetMonth((prevState) =>
      value === dateRange.current ? value : prevState + value
    );
  };

  const columns = [
    {
      title: intl.formatMessage({ id: "baselineDates.colProjectType" }),
      sorter: (a, b) =>
        a.projectTypeLabel.localeCompare(b.projectTypeLabel.toLowerCase()),
      defaultSortOrder: "ascend",
      fixed: "left",
      width: 150,
      render: ({ id, projectTypeId, projectTypeLabel }) => (
        <Link to={routes.admin.baselineDates.edit(projectTypeId, id)}>
          {projectTypeLabel}
        </Link>
      ),
    },
    {
      title: intl.formatMessage({ id: "baselineDates.colProductCategory" }),
      fixed: "left",
      width: 200,
      render: ({ productCategories }) =>
        productCategories?.map((tag) => (
          <Tag color={"blue"} key={tag}>
            {getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductCategory,
              tag
            )}
          </Tag>
        )),
    },
  ];

  const months = getMonths(offsetMonth);

  months.forEach((month) => {
    const key = month.format("MMM YY");
    const column = {
      title: key,
      dataIndex: key,
      width: 70,
      render: (record) => record?.map((x, index) => <p key={index}>{x}</p>),
    };
    columns.push(column);
  });

  const data = baselineDates?.map((date) => {
    let dateCols = {};
    date.dates?.forEach((d) => {
      const time = dayjs(d, DATE_FORMATS.fourDigitYear).format(
        DATE_FORMATS.fourDigitYear
      );
      const key = dayjs(time).format("MMM YY");
      dateCols = {
        ...dateCols,
        [key]: [...(dateCols[key] || []), dayjs(time).format("MM/DD")],
      };
    });

    return {
      projectTypeLabel: projectTypes?.[date.projectTypeId]?.fallback || "",
      ...date,
      ...dateCols,
    };
  });

  return (
    <PageWrapper pageTitle={intl.formatMessage({ id: `baselineDates.title` })}>
      <Row gutter={GUTTER_SIZE.large}>
        <GlobalS.PageContentCard
          title={intl.formatMessage({ id: "baselineDates.title" })}
          extra={
            <Button
              data-test-id="create-dates-btn"
              type="primary"
              icon={<FontAwesomeIcon icon={faCirclePlus} />}
              onClick={handleCreateDates}
            >
              {intl.formatMessage({ id: "general.add" })}
            </Button>
          }
        >
          <Row justify={"end"}>
            <Space>
              <Button
                data-test-id="previous-12-months"
                onClick={() =>
                  handleDateRangeChange(dateRange.previousMonthDouble)
                }
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </Button>
              <Button
                data-test-id="previous-3-months"
                onClick={() =>
                  handleDateRangeChange(dateRange.previousMonthSingle)
                }
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </Button>
              <Button
                data-test-id="current"
                onClick={() => handleDateRangeChange(dateRange.current)}
              >
                {intl.formatMessage({ id: "baselineDates.currentDateRange" })}
              </Button>
              <Button
                data-test-id="next-3-months"
                onClick={() => handleDateRangeChange(dateRange.nextMonthSingle)}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </Button>
              <Button
                data-test-id="next-12-months"
                onClick={() => handleDateRangeChange(dateRange.nextMonthDouble)}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </Button>
            </Space>
          </Row>
          <Row>
            <Col flex="auto">
              <Table
                data-test-id="baseline-dates-table"
                columns={columns}
                dataSource={data}
                rowKey={({ id }) => id}
                pagination
                loading={isLoading}
                scroll={{ x: 1500 }}
              />
            </Col>
          </Row>
        </GlobalS.PageContentCard>
      </Row>
    </PageWrapper>
  );
};

export default BaselineDates;
